import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { 
  Search, MapPin, Utensils, Bed, Car, Wrench, Home, Heart,
  Briefcase, GraduationCap, Stethoscope, Anchor, Plane, 
  ShoppingBag, Dumbbell, Sparkles, Theater, Building, Laptop,
  PawPrint, Flower, PartyPopper, ChevronRight, ArrowLeft,
  Star, Phone, Globe, Filter, Grid, List, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const categoryIcons = {
  'accommodation': Bed,
  'food-dining': Utensils,
  'arts-entertainment': Theater,
  'sporting-leisure': Dumbbell,
  'health-beauty': Sparkles,
  'automotive': Car,
  'shopping': ShoppingBag,
  'services': Wrench,
  'tradesman': Wrench,
  'business-finance': Briefcase,
  'builders': Building,
  'boats-boating': Anchor,
  'weddings': Heart,
  'party-hire': PartyPopper,
  'car-hire': Car,
  'it-web': Laptop,
  'office-services': Building,
  'real-estate': Home,
  'education': GraduationCap,
  'pets': PawPrint,
  'home-garden': Flower,
  'transport-travel': Plane,
  'tourism': MapPin,
  'medical': Stethoscope,
};

const CategoryPage = () => {
  const { cityId, categorySlug, subLocation } = useParams();
  const [searchParams] = useSearchParams();
  
  const [location, setLocation] = useState(null);
  const [category, setCategory] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [selectedSubLocation, setSelectedSubLocation] = useState(subLocation || 'all');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(0);
  const limit = 12;

  useEffect(() => {
    fetchData();
  }, [cityId, categorySlug, selectedSubcategory, selectedSubLocation, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch config for location and category info
      const configRes = await fetch(`${API_URL}/api/hotspots/config`);
      const configData = await configRes.json();
      
      // cityId might be undefined if using /hotspots/category/:categorySlug route
      const loc = cityId ? configData.locations.find(l => l.id === cityId) : null;
      const cat = configData.categories.find(c => c.slug === categorySlug);
      
      setLocation(loc);
      setCategory(cat);
      
      // Build query params for businesses
      let queryParams = `category_id=${cat?.id || categorySlug}&limit=${limit}&skip=${page * limit}`;
      
      // Only add location_id if we have a city
      if (cityId) {
        queryParams += `&location_id=${cityId}`;
      }
      
      if (selectedSubcategory && selectedSubcategory !== 'all') {
        queryParams += `&subcategory=${selectedSubcategory}`;
      }
      if (selectedSubLocation && selectedSubLocation !== 'all') {
        queryParams += `&sub_location=${selectedSubLocation}`;
      }
      
      // Fetch businesses
      const businessRes = await fetch(`${API_URL}/api/hotspots/businesses?${queryParams}`);
      const businessData = await businessRes.json();
      
      setBusinesses(businessData.businesses || []);
      setTotalCount(businessData.total || 0);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Trigger search
    fetchData();
  };

  const IconComponent = category ? (categoryIcons[category.id] || MapPin) : MapPin;

  if (loading && !category) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-pulse text-sky-600 text-xl">Loading...</div>
      </div>
    );
  }

  // Build breadcrumbs - handle case where no city is specified
  const breadcrumbItems = [
    { label: 'Australian Hotspots', href: '/hotspots' },
  ];
  
  if (cityId) {
    breadcrumbItems.push({ label: location?.name || cityId, href: `/hotspots/${cityId}` });
  }
  
  breadcrumbItems.push({ label: category?.name || categorySlug, href: null });

  if (subLocation) {
    const subLocName = location?.sub_locations?.find(s => s.slug === subLocation)?.name || subLocation;
    breadcrumbItems.push({ label: subLocName, href: null });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4 overflow-x-auto">
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                {item.href ? (
                  <Link to={item.href} className="hover:text-sky-600 whitespace-nowrap">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-slate-900 font-medium whitespace-nowrap">{item.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-sky-100 flex items-center justify-center">
                <IconComponent className="h-7 w-7 text-sky-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  {category?.name || categorySlug}{cityId ? ` in ${location?.name || cityId}` : ' Australia'}
                </h1>
                <p className="text-slate-500">
                  {totalCount} {totalCount === 1 ? 'business' : 'businesses'} found
                </p>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-9"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-9"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            {/* Sub-location filter */}
            {location?.sub_locations && location.sub_locations.length > 0 && (
              <Select value={selectedSubLocation} onValueChange={setSelectedSubLocation}>
                <SelectTrigger className="w-[180px]" data-testid="sublocation-filter">
                  <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {location.sub_locations.map((sub) => (
                    <SelectItem key={sub.slug} value={sub.slug}>
                      {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Subcategory filter */}
            {category?.subcategories && category.subcategories.length > 0 && (
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                <SelectTrigger className="w-[180px]" data-testid="subcategory-filter">
                  <Filter className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {category.name}</SelectItem>
                  {category.subcategories.map((sub) => (
                    <SelectItem key={sub.slug} value={sub.slug}>
                      {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Cuisine type filter for food & dining */}
            {category?.cuisine_types && category.cuisine_types.length > 0 && (
              <Select defaultValue="all">
                <SelectTrigger className="w-[180px]" data-testid="cuisine-filter">
                  <Utensils className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All cuisines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cuisines</SelectItem>
                  {category.cuisine_types.map((cuisine) => (
                    <SelectItem key={cuisine.slug} value={cuisine.slug}>
                      {cuisine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-sky-600">Loading businesses...</div>
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No businesses found</h3>
              <p className="text-slate-500 mb-6">
                We're still growing in this area. Be the first to list your business!
              </p>
              <Button className="bg-sky-600 hover:bg-sky-700">
                Add Your Business
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => {
                // Generate SEO-friendly URL - use business's own location/category if page doesn't have cityId
                const businessCity = cityId || business.location_id;
                const businessCategory = categorySlug || business.category_id;
                const businessUrl = business.slug 
                  ? `/hotspots/${businessCity}/${businessCategory}/business/${business.slug}`
                  : `/hotspots/business/${business.id}`;
                return (
                <Link
                  key={business.id}
                  to={businessUrl}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
                  data-testid={`business-card-${business.id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-slate-200 to-slate-300 relative">
                    {business.images?.[0] ? (
                      <img 
                        src={business.images[0]} 
                        alt={business.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <IconComponent className="h-12 w-12 text-slate-400" />
                      </div>
                    )}
                    {business.featured && (
                      <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        Featured
                      </div>
                    )}
                    {business.verified && (
                      <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        Verified
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-slate-900 group-hover:text-sky-600 transition-colors mb-1">
                      {business.name}
                    </h3>
                    {business.address && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                        <MapPin className="h-3 w-3" />
                        {business.address}
                      </p>
                    )}
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                      {business.description || 'Quality local business'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm text-slate-400">
                        {business.rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                            {business.rating.toFixed(1)}
                          </span>
                        )}
                        {business.price_range && (
                          <span className="text-emerald-600 font-medium">{business.price_range}</span>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-sky-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              )})}
            </div>
          ) : (
            <div className="space-y-4">
              {businesses.map((business) => {
                // Generate SEO-friendly URL - use business's own location/category if page doesn't have cityId
                const businessCity = cityId || business.location_id;
                const businessCategory = categorySlug || business.category_id;
                const businessUrl = business.slug 
                  ? `/hotspots/${businessCity}/${businessCategory}/business/${business.slug}`
                  : `/hotspots/business/${business.id}`;
                return (
                <Link
                  key={business.id}
                  to={businessUrl}
                  className="group flex bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                  data-testid={`business-list-${business.id}`}
                >
                  <div className="w-48 h-36 bg-gradient-to-br from-slate-200 to-slate-300 relative flex-shrink-0">
                    {business.images?.[0] ? (
                      <img 
                        src={business.images[0]} 
                        alt={business.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <IconComponent className="h-10 w-10 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-semibold text-lg text-slate-900 group-hover:text-sky-600 transition-colors">
                          {business.name}
                        </h3>
                        <div className="flex items-center gap-2">
                          {business.featured && (
                            <span className="bg-orange-100 text-orange-600 text-xs font-medium px-2 py-1 rounded-full">Featured</span>
                          )}
                          {business.verified && (
                            <span className="bg-emerald-100 text-emerald-600 text-xs font-medium px-2 py-1 rounded-full">Verified</span>
                          )}
                        </div>
                      </div>
                      {business.address && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                          <MapPin className="h-3 w-3" />
                          {business.address}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {business.description || 'Quality local business'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm mt-3">
                      {business.rating > 0 && (
                        <span className="flex items-center gap-1 text-slate-600">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          {business.rating.toFixed(1)} ({business.reviews_count || 0} reviews)
                        </span>
                      )}
                      {business.phone && (
                        <span className="flex items-center gap-1 text-slate-500">
                          <Phone className="h-4 w-4" />
                          {business.phone}
                        </span>
                      )}
                      {business.price_range && (
                        <span className="text-emerald-600 font-medium">{business.price_range}</span>
                      )}
                    </div>
                  </div>
                </Link>
              )})}
            </div>
          )}

          {/* Pagination */}
          {totalCount > limit && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-4">
                Page {page + 1} of {Math.ceil(totalCount / limit)}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= totalCount}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">
            About {category?.name || categorySlug}{cityId ? ` in ${location?.name || cityId}` : ' Australia'}
          </h2>
          <p className="text-slate-600 leading-relaxed">
            {category?.description || `Find the best ${categorySlug} businesses${cityId ? ` in ${location?.name || cityId}` : ' across Australia'}.`} 
            {' '}Browse our comprehensive directory of verified local businesses{cityId ? ` in ${location?.name || cityId}` : ' across Australia'}. 
            Whether you're a local or visitor, Australian Hotspots helps you discover quality 
            {' '}{category?.name?.toLowerCase() || categorySlug} services in your area.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Lionheart Digital Branding - POWERFUL */}
          <div className="flex flex-col items-center text-center mb-10">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl rounded-full"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/2fa9ddf7953c616448f48ed011e225e9e3469523a5a5c8f53c562308f1d829cc.png" 
                alt="Lionheart Digital" 
                className="h-28 md:h-36 w-auto relative z-10 drop-shadow-2xl"
              />
            </div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tight mb-3">
              <span className="text-white">LIONHEART</span>
              <span className="text-orange-500"> DIGITAL</span>
            </h3>
            <p className="text-lg md:text-xl font-bold italic text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-orange-400">
              "They lit the fire. We became it."
            </p>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center">
            <Link to="/hotspots" className="text-xl font-bold">
              <span className="text-sky-400">Australian</span>
              <span className="text-orange-400"> Hotspots</span>
            </Link>
            <p className="text-slate-400 text-sm mt-4">
              &copy; {new Date().getFullYear()} Australian Hotspots. A Lionheart Digital Asset.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CategoryPage;
